import { createSimpleStore } from '@/store/factories/simpleStoreFactory';
import { Dtos_V2_ExtraSystemDto } from '@/common/infrastructure/gql/generated/schema';

/**
 * Store for managing temporary extra systems data for jobs
 * Similar to JobCrewTemporaryStore but for extra systems
 */
export type JobExtraSystemsTemporaryState = {
  temporaryExtraSystemsByJob: Record<number, Dtos_V2_ExtraSystemDto[]>;
};

export type JobExtraSystemsTemporaryActions = {
  setTemporaryExtraSystems: (jobId: number, extraSystems: Dtos_V2_ExtraSystemDto[]) => void;
  addTemporaryExtraSystem: (jobId: number, extraSystem: Dtos_V2_ExtraSystemDto) => void;
  removeTemporaryExtraSystem: (jobId: number, extraSystemId: number) => void;
  getTemporaryExtraSystems: (jobId: number) => Dtos_V2_ExtraSystemDto[];
  clearJob: (jobId: number) => void;
  clearAll: () => void;
};

export const useJobExtraSystemsTemporaryStore = createSimpleStore<JobExtraSystemsTemporaryState, JobExtraSystemsTemporaryActions>({
  name: 'job-extra-systems-temporary',
  initialState: {
    temporaryExtraSystemsByJob: {}
  },
  actions: (set, get) => ({
    setTemporaryExtraSystems: (jobId: number, extraSystems: Dtos_V2_ExtraSystemDto[]) => {
      const next = { ...get().temporaryExtraSystemsByJob, [jobId]: extraSystems };
      set({ temporaryExtraSystemsByJob: next } as JobExtraSystemsTemporaryState);
    },
    addTemporaryExtraSystem: (jobId: number, extraSystem: Dtos_V2_ExtraSystemDto) => {
      const current = get().temporaryExtraSystemsByJob[jobId] || [];
      if (current.some((e) => e.id === extraSystem.id)) {
        // Update existing extra system
        const updated = current.map((e) => (e.id === extraSystem.id ? extraSystem : e));
        const next = { ...get().temporaryExtraSystemsByJob, [jobId]: updated };
        set({ temporaryExtraSystemsByJob: next } as JobExtraSystemsTemporaryState);
      } else {
        // Add new extra system
        const next = { ...get().temporaryExtraSystemsByJob, [jobId]: [...current, extraSystem] };
        set({ temporaryExtraSystemsByJob: next } as JobExtraSystemsTemporaryState);
      }
    },
    removeTemporaryExtraSystem: (jobId: number, extraSystemId: number) => {
      const current = get().temporaryExtraSystemsByJob[jobId] || [];
      const filtered = current.filter((e) => e.id !== extraSystemId);
      const next = { ...get().temporaryExtraSystemsByJob, [jobId]: filtered };
      set({ temporaryExtraSystemsByJob: next } as JobExtraSystemsTemporaryState);
    },
    getTemporaryExtraSystems: (jobId: number) => {
      return get().temporaryExtraSystemsByJob[jobId] || [];
    },
    clearJob: (jobId: number) => {
      const extraSystems = { ...get().temporaryExtraSystemsByJob };
      delete extraSystems[jobId];
      set({
        temporaryExtraSystemsByJob: extraSystems
      } as JobExtraSystemsTemporaryState);
    },
    clearAll: () => {
      set({
        temporaryExtraSystemsByJob: {}
      } as JobExtraSystemsTemporaryState);
    }
  })
});
